import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './client'

export type ProjectRole = 'manager' | 'inspector' | 'viewer'

export interface Project {
  id: string
  name: string
  organization: string | null
  description: string | null
  position_threshold_cm: number | null
  rotation_threshold_deg: number | null
  default_tolerance_mm: number | null
  created_at: string
  project_role: ProjectRole | null
}

export interface ProjectMember {
  id: string
  project_id: string
  user_id: string
  email: string
  full_name: string | null
  role: ProjectRole
  added_by_user_id: string | null
  created_at: string
}

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: async () => (await api.get<Project[]>('/api/v1/projects')).data,
  })
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: async () => (await api.get<Project>(`/api/v1/projects/${id}`)).data,
    enabled: Boolean(id),
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { name: string; organization?: string; description?: string }) =>
      (await api.post<Project>('/api/v1/projects', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  })
}

export function useProjectMembers(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['project-members', projectId],
    queryFn: async () =>
      (await api.get<ProjectMember[]>(`/api/v1/projects/${projectId}/members`)).data,
    enabled: Boolean(projectId) && enabled,
  })
}

export function useAddProjectMember(projectId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { email: string; role: ProjectRole }) =>
      (await api.post<ProjectMember>(`/api/v1/projects/${projectId}/members`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-members', projectId] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

export function useUpdateProjectMember(projectId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { membershipId: string; role: ProjectRole }) =>
      (
        await api.patch<ProjectMember>(
          `/api/v1/projects/${projectId}/members/${vars.membershipId}`,
          { role: vars.role },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-members', projectId] })
      qc.invalidateQueries({ queryKey: ['projects'] })
      qc.invalidateQueries({ queryKey: ['project', projectId] })
    },
  })
}

export function useRemoveProjectMember(projectId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (membershipId: string) =>
      api.delete(`/api/v1/projects/${projectId}/members/${membershipId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-members', projectId] })
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}

/**
 * 프로젝트 + 관련 자원(MinIO 파일 포함) 완전 삭제 — admin 전용.
 * 백엔드: DB CASCADE + S3 batch delete + hash chain `project.deleted`.
 */
export interface DeleteProjectResult {
  status: string
  project_id: string
  project_name: string
  counts: {
    bim_files: number
    site_photos: number
    inspection_reports: number
    s3_deleted: number
    s3_failed: number
  }
}

export function useDeleteProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (projectId: string) =>
      (await api.delete<DeleteProjectResult>(`/api/v1/projects/${projectId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] })
    },
  })
}
